/// <amd-module name="CodeCoverage/CodeCoverageTab/Services/CodeCoveragePipelineTabService" />
import { BuildStatus } from "Build/Client/Contracts/Build";
import { IRunViewContributedTabProps } from "Build/Run/RunViewContributedTab";
import { IRunDetailsService } from "Build/Run/RunDetailsService.types";
import { CodeCoverageSummary } from "Build/Run/RunExternalService";
import { BuildCodeCoverage } from "CodeCoverage/CodeCoverageTab/Components/BuildCodeCoverage";
import * as Resources from "CodeCoverage/CodeCoverageTab/Resources";
import { TelemetryEvents } from "CodeCoverage/Common/Constants";
import { RunViewArtifact } from "CodeCoverage/Common/RunViewArtifact";
import * as React from "react";
import { ObservableArray , IReadonlyObservableValue } from "VSS/Core/Observable";
import { ITabService } from "VSS/Features/PlatformUI/ContributedTab";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IVssContributedTab } from "VSSUI/Tabs.Types";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export class CodeCoveragePipelineTabService extends VssService implements ITabService<IRunViewContributedTabProps> {
    private _telemetryService: IVssTelemetryService;
    private codeCoverageTabEnabled: boolean = false;
    private tabs: ObservableArray<any>;
    private buildContext: IRunViewContributedTabProps;
    private tcmContribution: Promise<{} | undefined>;
    private contributionService: IVssContributionService;
    private tcmServiceContributionLoaded: boolean;
    private codeCoverageResults: IReadonlyObservableValue<CodeCoverageSummary>;
    private currentrunViewArtifact: RunViewArtifact;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this._telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        this.tabs = new ObservableArray<IVssContributedTab>();

        this.contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        this.getTcmContribution().then(() => {
            if (!this.tcmServiceContributionLoaded) {
                // wait for tcm location contribution to load
                this.tcmServiceContributionLoaded = true;

                // call this to ensure tab is rendered in case contribution took more time than expected
                this.onBuildStatusChanged();
            }
        });
    }
   
    public getTabs(id: string, buildContext?: IRunViewContributedTabProps) { 
        if (buildContext) {
            this.buildContext = buildContext;

            if (isFeatureFlagEnabled(this.pageContext, "WebAccess.TestManagement.CodeCoverage.EnableTabByDefault", false)) {
                if (buildContext.status) {
                    buildContext.status.subscribe(this.onBuildStatusChanged);
                }
                else {
                    this.onBuildStatusChanged();
                }
            }
            else {
                if (buildContext.status && buildContext.status.value !== BuildStatus.Completed) {
                    buildContext.status.subscribe(this.onBuildStatusChanged);
                } else {
                    this.onBuildStatusChanged();
                }
            }
        }
        return this.tabs;
    }

    private onBuildStatusChanged = () => {
        // Don't do anything if tab is already present

        if (isFeatureFlagEnabled(this.pageContext, "WebAccess.TestManagement.CodeCoverage.EnableTabByDefault", false) &&
            this.tcmServiceContributionLoaded &&
            this.buildContext) {
            this.currentrunViewArtifact = new RunViewArtifact(
                this.buildContext.runId.value,
                this.buildContext.projectId.value,
                this.buildContext.status.value
            );
            const runDetailsService = this.pageContext.getService<IRunDetailsService>("IRunDetailsService");
            this.codeCoverageResults = runDetailsService.codeCoverageResults;
            this.codeCoverageResults.subscribe(() => {
                this.createDefaultCodeCoverageTab();
            });
            if (this.codeCoverageResults.value) {
                this.createDefaultCodeCoverageTab();
            }
        }

       else if (
            this.tcmServiceContributionLoaded &&
            !this.codeCoverageTabEnabled &&
            this.buildContext &&
            this.buildContext.status.value === BuildStatus.Completed
        ) {
            const runViewArtifact = new RunViewArtifact(
                this.buildContext.runId.value,
                this.buildContext.projectId.value,
                this.buildContext.status.value
            );

            const runDetailsService = this.pageContext.getService<IRunDetailsService>("IRunDetailsService");
            this.codeCoverageResults = runDetailsService.codeCoverageResults;
            this.codeCoverageResults.subscribe(() => {
                this.createCodeCoverageTab (runViewArtifact);
            });
            if (this.codeCoverageResults.value) {
               this.createCodeCoverageTab (runViewArtifact);
            }
        } 
    };
    private createCodeCoverageTab(runViewArtifact: RunViewArtifact) {
        if (!this.codeCoverageTabEnabled &&
            ((this.codeCoverageResults.value &&
                'coverage' in this.codeCoverageResults.value &&
                this.codeCoverageResults.value.coverage))) {
            this.codeCoverageTabEnabled = true;
            this.tabs.push({
                id: "codecoverage-tab",
                render: () => {
                    return React.createElement(BuildCodeCoverage, { runViewArtifact });
                },
                name: Resources.CodeCoverage,
                onBeforeTabChange: () => {
                    this._telemetryService.publishEvent(TelemetryEvents.Area, TelemetryEvents.CodeCoverageTabClicked, {});
                    return true;
                }
            });
        }
    }

    private createDefaultCodeCoverageTab() {
        if (!this.codeCoverageTabEnabled) {
            this.codeCoverageTabEnabled = true;
            this.tabs.push({
                id: "codecoverage-tab",
                render: () => {
                    return React.createElement(BuildCodeCoverage, { runViewArtifact: this.currentrunViewArtifact });
                },
                name: Resources.CodeCoverage,
                onBeforeTabChange: () => {
                    this._telemetryService.publishEvent(TelemetryEvents.Area, TelemetryEvents.CodeCoverageTabClicked, {});
                    return true;
                }
            });
        }
    }

    private getTcmContribution(): Promise<{} | undefined> {
        if (!this.tcmContribution) {
            this.tcmContribution = this.contributionService.getContributionAsync("ms.vss-codecoverage-web.tcm-service-location");
        }

        return this.tcmContribution;
    }
}

export var CodeCoveragePipelineTabServiceName = "codecoverage-pipeline-tab-service";
Services.add(CodeCoveragePipelineTabServiceName, { serviceFactory: CodeCoveragePipelineTabService });
